import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react'
import { api, setImpersonationToken, clearImpersonationToken } from '@/services/api'

type ImpersonationState = {
  isImpersonating: boolean
  token: string | null
  orgId: string | null
  orgName: string | null
  expiresAt: string | null
}

type ImpersonationContextType = ImpersonationState & {
  startImpersonation: (orgId: string) => Promise<void>
  endImpersonation: () => Promise<void>
}

const INITIAL_STATE: ImpersonationState = {
  isImpersonating: false,
  token: null,
  orgId: null,
  orgName: null,
  expiresAt: null,
}

const ImpersonationContext = createContext<ImpersonationContextType | null>(null)

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ImpersonationState>(INITIAL_STATE)

  const endImpersonation = useCallback(async () => {
    if (state.token) {
      try {
        await api.adminEndImpersonation()
      } catch {
        // Best-effort — clear state regardless
      }
    }
    clearImpersonationToken()
    setState(INITIAL_STATE)
  }, [state.token])

  const startImpersonation = useCallback(async (orgId: string) => {
    const response = await api.adminStartImpersonation(orgId)
    setImpersonationToken(response.token)
    setState({
      isImpersonating: true,
      token: response.token,
      orgId: response.organizationId,
      orgName: response.organizationName,
      expiresAt: response.expiresAt,
    })
  }, [])

  // Auto-end when token expires
  // Cap at 2^31 - 1 ms (~24.8 days) to avoid setTimeout overflow
  const MAX_TIMEOUT_MS = 2_147_483_647

  useEffect(() => {
    if (!state.expiresAt) return

    const expiryMs = new Date(state.expiresAt).getTime() - Date.now()
    if (expiryMs <= 0) {
      void endImpersonation()
      return
    }

    const timer = setTimeout(() => {
      void endImpersonation()
    }, Math.min(expiryMs, MAX_TIMEOUT_MS))

    return () => { clearTimeout(timer) }
  }, [state.expiresAt, endImpersonation])

  return (
    <ImpersonationContext.Provider
      value={{
        ...state,
        startImpersonation,
        endImpersonation,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  )
}

export function useImpersonation(): ImpersonationContextType {
  const context = useContext(ImpersonationContext)
  if (!context) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider')
  }
  return context
}
